import { useSessionStorage } from "@/auth/storage";
import { createBackendClient } from "@/client/backend/client";
import type { components } from "@/client/backend/zz_schema";
import { PublicShell } from "@/components/layout/PublicShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { CheckCircledIcon, ExclamationTriangleIcon, ReloadIcon } from "@radix-ui/react-icons";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

type Organization = components["schemas"]["Organization"];

// Parse JWT payload without verification (verification happens on backend)
function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export default function OAuthAuthorize() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [sessionToken] = useSessionStorage("token");

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[] | "loading">("loading");
  // Use ref instead of state to prevent useEffect cleanup from canceling the auto-select timer
  const isAutoSelectingRef = useRef(false);
  const [isAutoSelectingDisplay, setIsAutoSelectingDisplay] = useState(false);

  const oauthSession = searchParams.get("oauth_session");
  const userToken = searchParams.get("token");

  // The URL param takes precedence when SSO starts this flow. Localhost falls
  // back to its tab-scoped token because the API session cookie is cross-site.
  const effectiveToken = userToken || sessionToken;

  // Parse the OAuth session JWT to get client info
  const sessionData = oauthSession ? parseJwtPayload(oauthSession) : null;
  const clientId = sessionData?.client_id as string | undefined;

  // Fetch user organizations using the token
  useEffect(() => {
    if (!effectiveToken) {
      setError("Missing authentication token. Please try logging in again.");
      return;
    }
    if (!oauthSession) {
      setError("Missing OAuth session. Please start the authorization flow again.");
      return;
    }

    const fetchOrganizations = async () => {
      try {
        const client = createBackendClient({
          baseUrl: import.meta.env.VITE_API_ENDPOINT as string,
          headers: {
            Authorization: `Bearer ${effectiveToken}`,
          },
        });

        // First get the user info to get username
        const whoami = await client.GET("/whoami");
        if (whoami.response.status !== 200 || !whoami.data?.user) {
          setError("Invalid or expired token. Please try logging in again.");
          return;
        }

        const userName = whoami.data.user.name;

        // Then get organizations
        const orgsResponse = await client.GET("/users/{userName}/memberships", {
          params: { path: { userName } },
        });

        if (orgsResponse.response.status !== 200 || !orgsResponse.data) {
          setError("Failed to fetch organizations.");
          return;
        }

        setOrganizations(orgsResponse.data);
      } catch (err) {
        console.error("Failed to fetch organizations:", err);
        setError("Failed to load your organizations. Please try again.");
      }
    };

    fetchOrganizations();
  }, [effectiveToken, oauthSession]);

  // Auto-select and auto-continue if user has only one organization
  useEffect(() => {
    if (organizations !== "loading" && organizations.length === 1 && oauthSession && effectiveToken && !isAuthorizing && !isAutoSelectingRef.current) {
      const singleOrg = organizations[0];
      setSelectedOrgId(singleOrg.id);
      // Use ref to prevent re-triggering, state for display only
      isAutoSelectingRef.current = true;
      setIsAutoSelectingDisplay(true);

      // Small delay to show the auto-selection before continuing
      const timer = setTimeout(() => {
        setIsAuthorizing(true);
        const backendUrl = import.meta.env.VITE_API_ENDPOINT || "";
        const completeUrl = new URL("/authn/oauth/complete", backendUrl);
        completeUrl.searchParams.set("oauth_session", oauthSession);
        completeUrl.searchParams.set("organization_id", singleOrg.id);
        completeUrl.searchParams.set("user_token", effectiveToken);
        window.location.href = completeUrl.toString();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [organizations, oauthSession, effectiveToken, isAuthorizing]);

  // If loading organizations, show loading state
  if (organizations === "loading") {
    if (error) {
      return (
        <PublicShell>
          <Card variant="plain" className="w-full max-w-md border-destructive">
            <CardHeader className="text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-destructive" />
              <CardTitle className="text-xl text-destructive">Authorization Error</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => window.close()}
                className="w-full"
                variant="outline"
              >
                Close Window
              </Button>
            </CardContent>
          </Card>
        </PublicShell>
      );
    }
    return (
      <PublicShell>
        <Card variant="plain" className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <ReloadIcon className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </PublicShell>
    );
  }

  // At this point, organizations is guaranteed to be an array
  const orgs = organizations;

  // Handle error state
  if (error) {
    return (
      <PublicShell>
        <Card variant="plain" className="w-full max-w-md border-destructive">
          <CardHeader className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-destructive" />
            <CardTitle className="text-xl text-destructive">Authorization Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => window.close()}
              className="w-full"
              variant="outline"
            >
              Close Window
            </Button>
          </CardContent>
        </Card>
      </PublicShell>
    );
  }

  // Handle case where user has no organizations
  if (orgs.length === 0) {
    return (
      <PublicShell>
        <Card variant="plain" className="w-full max-w-md">
          <CardHeader className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-warning" />
            <CardTitle className="text-xl">No Organizations</CardTitle>
            <CardDescription>
              You don't have any organizations. Please create one first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => window.location.href = "/"}
              className="w-full"
              variant="outline"
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </PublicShell>
    );
  }

  const handleAuthorize = async () => {
    if (!selectedOrgId) {
      toast({
        title: "Please select an organization",
        variant: "destructive",
      });
      return;
    }

    if (!oauthSession || !effectiveToken) {
      toast({
        title: "Session expired",
        description: "Please start the authorization flow again.",
        variant: "destructive",
      });
      return;
    }

    setIsAuthorizing(true);

    // Redirect to backend to complete the OAuth flow
    // The backend will verify the session and generate the auth code
    const backendUrl = import.meta.env.VITE_API_ENDPOINT || "";
    const completeUrl = new URL("/authn/oauth/complete", backendUrl);
    completeUrl.searchParams.set("oauth_session", oauthSession);
    completeUrl.searchParams.set("organization_id", selectedOrgId);
    completeUrl.searchParams.set("user_token", effectiveToken);

    // Redirect to backend to complete the flow
    window.location.href = completeUrl.toString();
  };

  return (
    <PublicShell>
      <Card variant="plain" className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <CheckCircledIcon className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Authorize Application</CardTitle>
          <CardDescription>
            {clientId ? (
              <>
                <span className="font-semibold text-primary">{clientId}</span> wants to access your Limrun account
              </>
            ) : (
              "An application wants to access your Limrun account"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border bg-muted/50 p-4">
            <h4 className="mb-2 text-sm font-medium">This application will be able to:</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircledIcon className="h-4 w-4 text-success" />
                Access MCP tools and resources
              </li>
              <li className="flex items-center gap-2">
                <CheckCircledIcon className="h-4 w-4 text-success" />
                Create and manage iOS instances
              </li>
              <li className="flex items-center gap-2">
                <CheckCircledIcon className="h-4 w-4 text-success" />
                Use compute minutes from your organization
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">
              {orgs.length === 1 ? "Organization" : "Select Organization"}
            </label>
            {orgs.length === 1 ? (
              <div className="flex items-center gap-2 p-3 rounded-md border bg-muted">
                <CheckCircledIcon className="h-4 w-4 text-success" />
                <span>{orgs[0].displayName || orgs[0].id}</span>
                {isAutoSelectingDisplay && (
                  <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                    <ReloadIcon className="h-3 w-3 animate-spin" />
                    Auto-authorizing...
                  </span>
                )}
              </div>
            ) : (
              <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((org) => (
                    <SelectItem
                      key={org.id}
                      value={org.id}
                    >
                      {org.displayName || org.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              The application will have access to this organization's resources.
            </p>
          </div>

          {!isAutoSelectingDisplay && (
            <div className="flex gap-3">
              <Button
                onClick={() => window.close()}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAuthorize}
                disabled={isAuthorizing || !selectedOrgId}
                className="flex-1"
              >
                {isAuthorizing && (
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isAuthorizing ? "Authorizing..." : "Authorize"}
              </Button>
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground">
            By authorizing, you agree to share your organization data with this application.
            You can revoke access at any time from your settings.
          </p>
        </CardContent>
      </Card>
    </PublicShell>
  );
}
